// FAMILY-EXT: Модал настроек доски — открывается из гамбургер-меню.
// Внутри: переименование + фон проекта (градиент или картинка) +
// кнопка удалить доску с confirm. Раньше фон редактировался только в
// настройках проекта; теперь он же доступен и здесь, чтобы пользователю
// не приходилось переходить между двумя модалами.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal } from 'semantic-ui-react';
import { Input } from '../../lib/custom-ui';

import BackgroundPane from '../ProjectSettingsModal/BackgroundPane';

import styles from './BoardSettingsModal.module.scss';

const BoardSettingsModal = React.memo(
  ({
    name,
    onUpdate,
    onDelete,
    onClose,
    // FAMILY-EXT: данные фона проекта
    projectBackground,
    projectBackgroundImage,
    isProjectBackgroundImageUpdating,
    onProjectUpdate,
    onProjectBackgroundImageUpdate,
  }) => {
    const [t] = useTranslation();
    const [value, setValue] = useState(name || '');
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
      setValue(name || '');
    }, [name]);

    useEffect(() => {
      if (inputRef.current && inputRef.current.select) {
        inputRef.current.select();
      }
    }, []);

    const handleSubmit = useCallback(() => {
      const trimmed = value.trim();
      if (!trimmed) {
        if (inputRef.current && inputRef.current.select) inputRef.current.select();
        return;
      }
      if (trimmed !== name) {
        onUpdate({ name: trimmed });
      }
      onClose();
    }, [value, name, onUpdate, onClose]);

    const handleDeleteClick = useCallback(() => {
      if (!confirmingDelete) {
        setConfirmingDelete(true);
        return;
      }
      onDelete();
      onClose();
    }, [confirmingDelete, onDelete, onClose]);

    // FAMILY-EXT: handlers для BackgroundPane — мапят на updateCurrentProject
    const handleBackgroundUpdate = useCallback(
      (background) => {
        onProjectUpdate({ background });
      },
      [onProjectUpdate],
    );

    const handleBackgroundImageDelete = useCallback(() => {
      onProjectUpdate({ backgroundImage: null });
    }, [onProjectUpdate]);

    return (
      <Modal open closeIcon size="small" centered={false} onClose={onClose}>
        <Modal.Header>{t('common.editBoard', { context: 'title' })}</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleSubmit}>
            <div className={styles.label}>{t('common.title')}</div>
            <Input
              fluid
              ref={inputRef}
              value={value}
              onChange={(_, data) => setValue(data.value)}
            />
            <div className={styles.actions}>
              <Button positive content={t('action.save')} />
              <Button
                type="button"
                negative={confirmingDelete}
                content={
                  confirmingDelete
                    ? t('action.deleteBoard', { context: 'title' })
                    : t('action.delete')
                }
                onClick={handleDeleteClick}
                className={styles.deleteBtn}
              />
            </div>
          </Form>

          {/* FAMILY-EXT: фон проекта (градиенты + картинка) */}
          <div className={styles.backgroundSection}>
            <div className={styles.label}>{t('common.background', { context: 'title' })}</div>
            <BackgroundPane
              item={projectBackground}
              imageCoverUrl={projectBackgroundImage && projectBackgroundImage.coverUrl}
              isImageUpdating={isProjectBackgroundImageUpdating}
              onUpdate={handleBackgroundUpdate}
              onImageUpdate={onProjectBackgroundImageUpdate}
              onImageDelete={handleBackgroundImageDelete}
            />
          </div>
        </Modal.Content>
      </Modal>
    );
  },
);

BoardSettingsModal.propTypes = {
  name: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // FAMILY-EXT
  /* eslint-disable react/forbid-prop-types */
  projectBackground: PropTypes.object,
  projectBackgroundImage: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */
  isProjectBackgroundImageUpdating: PropTypes.bool,
  onProjectUpdate: PropTypes.func.isRequired,
  onProjectBackgroundImageUpdate: PropTypes.func.isRequired,
};

BoardSettingsModal.defaultProps = {
  name: '',
  projectBackground: undefined,
  projectBackgroundImage: undefined,
  isProjectBackgroundImageUpdating: false,
};

export default BoardSettingsModal;
